<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-12 d-flex justify-content-center">
        <div class="s-20 font-weight-600 text-black">Continue Payment with</div>
      </div>
    </div>

    <!-- DYNAMIC PAYMENT BUTTONS -->
    <div
      v-for="gateway in availableGateways"
      :key="gateway.name"
      class="row row-button d-flex justify-content-center"
      @click="initializePayment(gateway.name)"
    >
      <div class="col-8 col-sm-6" v-if="gateway.image">
        <img
          class="w-100 img-height"
          :src="gateway.image"
          :alt="gateway.name"
        />
      </div>
      <div v-else>
        <span>{{ gateway.name }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watchEffect } from "vue";
import axios from "@/gateway/backendapi";
import { ElMessage } from "element-plus";

export default {
  props: {
    formData: Object,
    donation: Boolean,
    close: Object,
    amount: Number,
    converted: Number,
    name: String,
    email: String,
    currency: String,
    callPayment: Boolean,
    initializePaymentResponse: Object,
  },

  emits: [
    "selected-gateway",
    "transaction-reference",
    "paystack-amount",
    "payment-successful",
    "resetcallpaymentprops",
  ],

  setup(props, { emit }) {
    const selectedGateway = ref("");

    const normalizeImagePath = (path) => {
      if (!path) return "";
      if (path.startsWith("http") || path.startsWith("https")) {
        return path;
      }
      const cleanPath = path.replace(/(\.\.\/)+/, "").trim();

      try {
        return require(`@/assets/${cleanPath.split("/").pop()}`);
      } catch (err) {
        console.log("Image not found:", cleanPath);
        return "";
      }
    };

    // ------------------------------------------------------
    // NORMALIZE ALL BACKEND GATEWAY STRUCTURES
    // ------------------------------------------------------
    const availableGateways = computed(() => {
      let gateways = [];

      // 1️⃣ paymentGateWayOptions
      if (props.formData?.paymentGateWayOptions?.length) {
        gateways = props.formData.paymentGateWayOptions.map((i) => ({
          name:
            i.churchPaymentGateway?.gateway?.name ||
            i.churchPaymentGateway?.displayName,
          subAccountId: i.churchPaymentGateway?.config?.subAccountId,
          image: normalizeImagePath(i.churchPaymentGateway?.gateway?.imageUrl),
        }));
      }

      // 2️⃣ paymentGateWays
      else if (props.formData?.paymentGateWays?.length) {
        gateways = props.formData.paymentGateWays.map((i) => ({
          name: i.paymentGateway?.name,
          subAccountId: i.subAccountID,
          image: normalizeImagePath(i.paymentGateway?.imageUrl),
        }));
      }

      // 3️⃣ fallback gatewayButtons
      else if (props.formData?.gatewayButtons?.length) {
        gateways = props.formData.gatewayButtons.map((i) => ({
          name: i.name,
          subAccountId: null,
          image: normalizeImagePath(i.buttonImagePath),
        }));
      }

      return gateways.filter(Boolean);
    });

    const getSubAccountId = (name) =>
      availableGateways.value.find(
        (g) => g.name?.toLowerCase() === name.toLowerCase()
      )?.subAccountId;

    // ------------------------------------------------------
    // SELECT GATEWAY
    // ------------------------------------------------------
    const initializePayment = (gatewayName) => {
      selectedGateway.value = gatewayName;
      emit("selected-gateway", gatewayName);
    };

    // ------------------------------------------------------
    // CONFIRM DONATION PAYMENT
    // ------------------------------------------------------
    const confirmDonationPayment = async (trans_id, tx_ref) => {
      try {
        const { data } = await axios.post(
          `/ConfirmDonationPayment?id=${trans_id}&txnref=${tx_ref}`
        );
        if (data.status) {
          emit("payment-successful", true);
        } else {
          throw new Error("Confirmation failed");
        }
      } catch {
        ElMessage({
          type: "error",
          showClose: true,
          message:
            "Confirmation failed. Please contact support at info@churchplus.co",
          duration: 8000,
        });
      }
    };

    // ------------------------------------------------------
    // PAYSTACK PAYMENT
    // ------------------------------------------------------
    const payWithPaystack = (initRes) => {
      props.close?.click?.();

      /* global PaystackPop */
      const handler = PaystackPop.setup({
        key: process.env.VUE_APP_PAYSTACK_PUBLIC_KEY_LIVE,
        email: props.email,
        amount: (props.converted || props.amount) * 100,
        firstname: props.name,
        ref: initRes.transactionReference,
        channels: [
          "card",
          "bank",
          "ussd",
          "qr",
          "mobile_money",
          "bank_transfer",
        ],
        subaccount: getSubAccountId("Paystack"),
        bearer: "subaccount",
        onClose() {
          ElMessage({
            type: "info",
            showClose: true,
            message: "Transaction cancelled",
            duration: 4000,
          });
        },
        callback(response) {
          emit("transaction-reference", response.trxref);
          emit("paystack-amount");
          confirmDonationPayment(response.trxref, response.trxref);
        },
      });

      handler.openIframe();
    };

    // ------------------------------------------------------
    // FLUTTERWAVE PAYMENT
    // ------------------------------------------------------
    const getFlutterwaveModules = () => {
      const script = document.createElement("script");
      script.src = "https://checkout.flutterwave.com/v3.js";
      document.head.appendChild(script);
    };
    getFlutterwaveModules();

    const payWithFlutterwave = (initRes) => {
      props.close?.click?.();

      window.FlutterwaveCheckout({
        public_key: process.env.VUE_APP_FLUTTERWAVE_PUBLIC_KEY_LIVE,
        tx_ref: initRes.transactionReference,
        amount: props.amount,
        currency: props.currency,
        payment_options: "card,ussd",
        customer: { name: props.name, email: props.email },
        subaccounts: [{ id: getSubAccountId("FlutterWave") }],
        callback: (response) => {
          emit("transaction-reference", response.transaction_id);
          confirmDonationPayment(response.transaction_id, response.tx_ref);
        },
        onclose: () => console.log("Payment closed"),
        customizations: {
          title: props.formData.churchName,
          description: "Payment for contribution items",
          logo: props.formData.churchLogo,
        },
      });
    };

    // ------------------------------------------------------
    // WATCH FOR CALL PAYMENT TRIGGER
    // ------------------------------------------------------
    watchEffect(() => {
      if (!props.formData?.isPaymentConnected) {
        if (
          props.callPayment &&
          props.initializePaymentResponse &&
          selectedGateway.value
        ) {
          const g = selectedGateway.value.toLowerCase();
          if (g === "paystack")
            payWithPaystack(props.initializePaymentResponse);
          else if (g === "flutterwave")
            payWithFlutterwave(props.initializePaymentResponse);
          emit("resetcallpaymentprops", false);
        }
      }
    });

    return {
      availableGateways,
      initializePayment,
    };
  },
};
</script>

<style scoped>
.row-button {
  padding: 10px;
  border-radius: 25px;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
  background: white;
  margin: 12px 70px;
  transition: all 0.3s ease-in-out;
  max-height: 45px;
}

.row-button:hover {
  cursor: pointer;
  transform: scale(1.05);
}

.img-pay {
  width: 125px;
}

.img-height {
  max-height: 36px;
}
</style>
